import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

// ─── Fonts & Global CSS ──────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');`;

const css = `
  *{box-sizing:border-box;margin:0;padding:0}
  :root{
    --gold:#e8d5a0;--gold-dim:#b8a870;--gold-bright:#f4e8bc;
    --crimson:#8b2635;--crimson-light:#a83245;
    --bg:#0a0a0a;--surface:#111111;
    --text:#e8d5a0;--text-dim:#8a7e68;
  }
  body{background:var(--bg);color:var(--text);font-family:'Outfit',sans-serif}
  html{scroll-behavior:smooth}

  /* ── NAV ── */
  .nav{position:fixed;top:0;left:0;right:0;z-index:200;padding:0 2rem;transition:all .3s}
  .nav.scrolled{background:rgba(10,10,10,.95);backdrop-filter:blur(14px);border-bottom:1px solid rgba(232,213,160,.07)}
  .nav-inner{max-width:1200px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;padding:1.1rem 0}
  .logo{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:700;letter-spacing:2px;background:linear-gradient(135deg,#f4e8bc,#c8a84b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;cursor:pointer;border:none;background-color:transparent}
  .nav-links{display:flex;align-items:center;gap:2rem}
  .nav-link{color:var(--text-dim);font-size:.9rem;font-weight:500;cursor:pointer;transition:color .2s;background:none;border:none;font-family:'Outfit',sans-serif}
  .nav-link:hover{color:var(--gold)}
  .nav-cta{background:linear-gradient(135deg,#e8d5a0,#c8a84b);color:#0a0a0a;border:none;padding:.55rem 1.4rem;border-radius:30px;font-size:.9rem;font-weight:600;cursor:pointer;transition:all .2s;font-family:'Outfit',sans-serif}
  .nav-cta:hover{transform:translateY(-1px);box-shadow:0 4px 16px rgba(232,213,160,.3)}
  .nav-dashboard{background:transparent;color:var(--gold-dim);border:1.5px solid rgba(232,213,160,.3);padding:.5rem 1.2rem;border-radius:30px;font-size:.88rem;font-weight:600;cursor:pointer;transition:all .2s;font-family:'Outfit',sans-serif;margin-right:.5rem}
  .nav-dashboard:hover{border-color:var(--gold-dim);color:var(--gold)}
  .nav-hamburger{display:none;flex-direction:column;gap:5px;cursor:pointer;background:none;border:none;padding:.25rem}
  .nav-hamburger span{display:block;width:24px;height:2px;background:var(--gold-dim);border-radius:2px;transition:all .3s}
  .nav-hamburger.open span:nth-child(1){transform:translateY(7px) rotate(45deg)}
  .nav-hamburger.open span:nth-child(2){opacity:0}
  .nav-hamburger.open span:nth-child(3){transform:translateY(-7px) rotate(-45deg)}

  /* ── MOBILE DRAWER ── */
  .mobile-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:300;opacity:0;pointer-events:none;transition:opacity .3s}
  .mobile-overlay.open{opacity:1;pointer-events:all}
  .mobile-drawer{position:fixed;top:0;right:0;bottom:0;width:260px;background:#111;z-index:400;transform:translateX(100%);transition:transform .3s ease;padding:2rem 1.5rem;display:flex;flex-direction:column;gap:.5rem;border-left:1px solid rgba(232,213,160,.07)}
  .mobile-drawer.open{transform:translateX(0)}
  .mobile-drawer-close{align-self:flex-end;background:none;border:none;color:var(--text-dim);font-size:1.4rem;cursor:pointer;margin-bottom:1rem;line-height:1}
  .mobile-nav-link{background:none;border:none;color:var(--text-dim);font-size:1rem;font-weight:500;font-family:'Outfit',sans-serif;padding:.75rem 0;cursor:pointer;text-align:left;border-bottom:1px solid rgba(232,213,160,.06);width:100%;transition:color .2s}
  .mobile-nav-link:hover{color:var(--gold)}
  .mobile-cta{width:100%;margin-top:1rem;padding:.75rem;background:linear-gradient(135deg,#e8d5a0,#c8a84b);color:#0a0a0a;border:none;border-radius:30px;font-size:1rem;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif}
  .mobile-dashboard{width:100%;margin-top:.5rem;padding:.7rem;background:transparent;color:var(--gold-dim);border:1.5px solid rgba(232,213,160,.3);border-radius:30px;font-size:.95rem;font-weight:600;cursor:pointer;font-family:'Outfit',sans-serif}

  /* ── HERO ── */
  .hero{min-height:100vh;display:flex;align-items:center;position:relative;overflow:hidden;
    background:radial-gradient(ellipse at 15% 60%,rgba(139,38,53,.12) 0%,transparent 55%),
    radial-gradient(ellipse at 85% 30%,rgba(200,168,75,.06) 0%,transparent 50%),var(--bg)}
  .hero::before{content:'';position:absolute;inset:0;
    background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23e8d5a0' fill-opacity='0.018'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");pointer-events:none}
  .hero-inner{max-width:1200px;margin:0 auto;padding:8rem 2rem 4rem;display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:center}
  .hero-eyebrow{display:inline-flex;align-items:center;gap:.5rem;background:rgba(139,38,53,.2);border:1px solid rgba(139,38,53,.4);color:#a83245;padding:.35rem 1rem;border-radius:20px;font-size:.78rem;font-weight:600;letter-spacing:1px;text-transform:uppercase;margin-bottom:1.5rem;width:fit-content}
  .hero-h1{font-family:'Cormorant Garamond',serif;font-size:clamp(2.8rem,5vw,4.2rem);font-weight:700;line-height:1.1;color:#fff;margin-bottom:1.5rem}
  .gold-grad{background:linear-gradient(135deg,#f4e8bc,#c8a84b);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
  .hero-sub{color:var(--text-dim);font-size:1.05rem;line-height:1.75;margin-bottom:2.5rem;max-width:490px}
  .hero-sub strong{color:var(--gold);font-weight:600}
  .hero-btns{display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:.85rem}
  .btn-primary{background:linear-gradient(135deg,#e8d5a0,#c8a84b);color:#0a0a0a;border:none;padding:.85rem 2.2rem;border-radius:30px;font-size:1rem;font-weight:700;cursor:pointer;transition:all .2s;font-family:'Outfit',sans-serif}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(232,213,160,.35)}
  .btn-outline{background:transparent;color:var(--gold-dim);border:1.5px solid rgba(232,213,160,.3);padding:.85rem 2.2rem;border-radius:30px;font-size:1rem;font-weight:600;cursor:pointer;transition:all .2s;font-family:'Outfit',sans-serif}
  .btn-outline:hover{border-color:var(--gold-dim);color:var(--gold)}
  .hero-microcopy{color:var(--text-dim);font-size:.8rem;margin-bottom:1.5rem}
  .hero-trust{display:flex;gap:2rem;flex-wrap:wrap;padding-top:1.5rem;border-top:1px solid rgba(232,213,160,.08)}
  .trust-item{display:flex;align-items:center;gap:.4rem}
  .trust-dot{width:5px;height:5px;border-radius:50%;background:var(--crimson)}
  .trust-text{color:var(--text-dim);font-size:.8rem}

  /* ── DASHBOARD MOCKUP ── */
  .db-frame{background:#121212;border:1px solid rgba(255,255,255,.08);border-radius:14px;overflow:hidden;box-shadow:0 40px 80px rgba(0,0,0,.6)}
  .db-titlebar{background:#1c1c1c;padding:.55rem 1rem;display:flex;align-items:center;gap:.4rem;border-bottom:1px solid rgba(255,255,255,.05)}
  .dt{width:10px;height:10px;border-radius:50%;flex-shrink:0}
  .dt-r{background:#ff5f57}.dt-y{background:#febc2e}.dt-g{background:#28c840}
  .db-urlbar{margin-left:.5rem;font-size:.65rem;color:rgba(255,255,255,.22);font-family:monospace}
  .db-body{padding:1.4rem}
  .db-greeting-title{font-size:.95rem;font-weight:700;color:rgba(255,255,255,.88);margin-bottom:.15rem}
  .db-greeting-sub{font-size:.7rem;color:rgba(255,255,255,.38);margin-bottom:1.2rem}
  .db-stats{display:grid;grid-template-columns:repeat(5,1fr);gap:.5rem;margin-bottom:.85rem}
  .db-stat{background:linear-gradient(145deg,#1e1e1e,#262626);border:1px solid rgba(255,255,255,.06);border-radius:12px;padding:.8rem .55rem;cursor:pointer;transition:transform .2s;position:relative;display:flex;flex-direction:column;gap:.45rem}
  .db-stat:hover{transform:translateY(-2px)}
  .db-stat-icon{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:.88rem;flex-shrink:0}
  .db-stat-label{font-size:.57rem;font-weight:600;color:rgba(255,255,255,.38);text-transform:uppercase;letter-spacing:.04em;line-height:1.2}
  .db-stat-num{font-size:1.2rem;font-weight:700;color:rgba(255,255,255,.9);line-height:1}
  .db-pulse-badge{position:absolute;top:6px;right:6px;background:#ff1744;color:#fff;border-radius:50%;width:16px;height:16px;font-size:.55rem;font-weight:700;display:flex;align-items:center;justify-content:center}
  .db-new-chip{display:inline-block;margin-top:.2rem;padding:.08rem .32rem;background:rgba(255,23,68,.12);color:#ff1744;font-size:.54rem;font-weight:700;border-radius:7px}
  .db-actions{display:grid;grid-template-columns:repeat(3,1fr);gap:.5rem;margin-bottom:.85rem}
  .db-action{background:linear-gradient(145deg,#1e1e1e,#262626);border:1px solid rgba(255,255,255,.06);border-radius:12px;padding:.7rem .65rem;display:flex;align-items:center;gap:.5rem;position:relative;overflow:hidden;cursor:pointer}
  .db-action::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px}
  .db-action-green::after{background:#43a047}
  .db-action-blue::after{background:#2979ff}
  .db-action-purple::after{background:#7c4dff}
  .db-action-icon{width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:.82rem;flex-shrink:0}
  .db-action-title{font-size:.68rem;font-weight:700;color:rgba(255,255,255,.8)}
  .db-action-sub{font-size:.58rem;color:rgba(255,255,255,.33)}
  .db-section{background:linear-gradient(145deg,#1e1e1e,#262626);border:1px solid rgba(255,255,255,.06);border-radius:12px;overflow:hidden}
  .db-section-hd{display:flex;align-items:center;justify-content:space-between;padding:.75rem 1rem}
  .db-section-left{display:flex;align-items:center;gap:.6rem}
  .db-section-icon{width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:.78rem;flex-shrink:0}
  .db-section-title{font-size:.76rem;font-weight:700;color:rgba(255,255,255,.85)}
  .db-section-sub{font-size:.6rem;color:rgba(255,255,255,.33)}
  .db-chip-blue{display:inline-block;padding:.13rem .48rem;border-radius:8px;font-size:.58rem;font-weight:600;background:rgba(41,121,255,.12);color:#2979ff}
  .db-appt-item{display:flex;align-items:flex-start;gap:.55rem;padding:.55rem .9rem;border-top:1px solid rgba(255,255,255,.04)}
  .db-appt-avatar{width:26px;height:26px;border-radius:50%;background:rgba(255,23,68,.12);color:#ff1744;font-size:.68rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:.05rem}
  .db-appt-name{font-size:.72rem;font-weight:600;color:rgba(255,255,255,.8)}
  .db-appt-time{font-size:.6rem;color:rgba(255,255,255,.35);margin-top:.08rem}
  .db-appt-badge{display:inline-block;font-size:.57rem;font-weight:600;padding:.08rem .38rem;border-radius:6px;margin-left:.3rem}
  .badge-today{background:rgba(255,23,68,.14);color:#ff1744}
  .badge-tmrw{background:rgba(255,109,0,.14);color:#ff6d00}
  .badge-days{background:rgba(255,255,255,.07);color:rgba(255,255,255,.38)}

  /* ── INQUIRY FORM MOCKUP ── */
  .inq-frame{background:#fff;border-radius:14px;box-shadow:0 25px 60px rgba(0,0,0,.45);overflow:hidden}
  .inq-browser-bar{background:#f5f5f5;padding:.48rem .85rem;display:flex;align-items:center;gap:.32rem;border-bottom:1px solid #e0e0e0}
  .inq-dt{width:9px;height:9px;border-radius:50%}
  .inq-dt-r{background:#ff5f57}.inq-dt-y{background:#febc2e}.inq-dt-g{background:#28c840}
  .inq-url{margin-left:.4rem;font-size:.6rem;color:#9e9e9e;font-family:monospace}
  .inq-body{padding:1.25rem;background:#f5f5f5;display:flex;flex-direction:column;gap:.75rem}
  .inq-header-paper{background:#fff;border-radius:10px;padding:1rem 1.25rem;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.07)}
  .inq-title{font-size:.95rem;font-weight:700;color:#212121;margin-bottom:.18rem;font-family:'Outfit',sans-serif}
  .inq-studio{font-size:.78rem;color:#757575;font-family:'Outfit',sans-serif}
  .inq-form-paper{background:#fff;border-radius:10px;padding:1.1rem 1.25rem;box-shadow:0 1px 3px rgba(0,0,0,.07)}
  .inq-sec{display:flex;align-items:center;gap:.4rem;font-size:.75rem;font-weight:700;color:#212121;margin-bottom:.7rem;font-family:'Outfit',sans-serif}
  .inq-row2{display:grid;grid-template-columns:1fr 1fr;gap:.6rem;margin-bottom:.6rem}
  .inq-row1{margin-bottom:.6rem}
  .inq-field{display:flex;flex-direction:column;gap:.22rem}
  .inq-lbl{font-size:.6rem;font-weight:600;color:#616161;text-transform:uppercase;letter-spacing:.03em;font-family:'Outfit',sans-serif}
  .inq-inp{position:relative}
  .inq-inp-icon{position:absolute;left:.5rem;top:50%;transform:translateY(-50%);font-size:.78rem;color:#9e9e9e;pointer-events:none;line-height:1}
  .inq-input{width:100%;background:#fafafa;border:1px solid #e0e0e0;border-radius:6px;padding:.42rem .5rem .42rem 1.75rem;font-size:.75rem;color:#424242;font-family:'Outfit',sans-serif;outline:none}
  .inq-input::placeholder{color:#bdbdbd}
  .inq-select{width:100%;background:#fafafa;border:1px solid #e0e0e0;border-radius:6px;padding:.42rem .5rem;font-size:.75rem;color:#424242;font-family:'Outfit',sans-serif;outline:none}
  .inq-textarea-wrap{position:relative}
  .inq-ta-icon{position:absolute;left:.5rem;top:.5rem;font-size:.78rem;color:#9e9e9e;pointer-events:none;line-height:1}
  .inq-textarea{width:100%;background:#fafafa;border:1px solid #e0e0e0;border-radius:6px;padding:.42rem .5rem .42rem 1.75rem;font-size:.75rem;color:#424242;font-family:'Outfit',sans-serif;min-height:52px;resize:none;line-height:1.4;outline:none}
  .inq-divider{border:none;border-top:1px solid #f0f0f0;margin:.7rem 0}
  .inq-submit{width:100%;padding:.62rem;background:#1976d2;color:#fff;border:none;border-radius:8px;font-size:.82rem;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;margin-top:.5rem;display:flex;align-items:center;justify-content:center;gap:.4rem}
  .inq-note{font-size:.6rem;color:#9e9e9e;text-align:center;margin-top:.4rem;font-family:'Outfit',sans-serif}

  /* ── SHARED SECTION STYLES ── */
  .section{padding:6rem 2rem}
  .section-alt{background:var(--surface)}
  .section-inner{max-width:1200px;margin:0 auto}
  .section-header{text-align:center;margin-bottom:3.5rem}
  .eyebrow{display:inline-block;font-size:.72rem;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:var(--crimson-light);margin-bottom:.8rem}
  .section-title{font-family:'Cormorant Garamond',serif;font-size:clamp(2rem,3.5vw,2.8rem);font-weight:700;color:#fff;margin-bottom:.75rem;line-height:1.2}
  .section-sub{color:var(--text-dim);font-size:.97rem;max-width:540px;margin:0 auto;line-height:1.7}

  /* ── PAIN STRIP ── */
  .pain-strip{background:var(--surface);border-top:1px solid rgba(232,213,160,.05);border-bottom:1px solid rgba(232,213,160,.05);padding:3rem 2rem}
  .pain-inner{max-width:960px;margin:0 auto;text-align:center}
  .pain-eyebrow{font-size:.72rem;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:var(--crimson-light);margin-bottom:.9rem}
  .pain-title{font-family:'Cormorant Garamond',serif;font-size:1.9rem;font-weight:600;color:rgba(255,255,255,.8);margin-bottom:1.8rem}
  .pain-items{display:flex;gap:.7rem;justify-content:center;flex-wrap:wrap}
  .pain-item{background:rgba(139,38,53,.08);border:1px solid rgba(139,38,53,.2);padding:.52rem 1.1rem;border-radius:30px;font-size:.84rem;color:var(--text-dim);display:flex;align-items:center;gap:.42rem}
  .pain-x{color:var(--crimson-light)}

  /* ── VALUE PILLARS ── */
  .pillars-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem}
  .pillar-card{background:rgba(232,213,160,.03);border:1px solid rgba(232,213,160,.08);border-radius:12px;padding:2rem;transition:all .3s}
  .pillar-card:hover{border-color:rgba(139,38,53,.3);transform:translateY(-4px);box-shadow:0 12px 30px rgba(0,0,0,.2)}
  .pillar-icon{width:50px;height:50px;border-radius:50%;background:linear-gradient(135deg,#8b2635,#a83245);display:flex;align-items:center;justify-content:center;font-size:1.25rem;margin-bottom:1.1rem;box-shadow:0 4px 12px rgba(139,38,53,.3)}
  .pillar-title{font-size:1.1rem;font-weight:700;color:#fff;margin-bottom:.25rem}
  .pillar-sub{font-size:.8rem;color:var(--text-dim);font-style:italic;margin-bottom:1.1rem}
  .pillar-points{list-style:none}
  .pillar-points li{display:flex;align-items:flex-start;gap:.52rem;color:var(--text-dim);font-size:.85rem;line-height:1.5;margin-bottom:.7rem}
  .check{color:var(--crimson-light);font-size:.85rem;flex-shrink:0;margin-top:.1rem}

  /* ── INQUIRY FEATURE SECTION ── */
  .inquiry-section{padding:6rem 2rem;background:linear-gradient(135deg,rgba(139,38,53,.1) 0%,rgba(10,10,10,0) 55%),var(--bg);position:relative;overflow:hidden}
  .inquiry-section::before{content:'';position:absolute;top:-80px;right:-80px;width:380px;height:380px;background:radial-gradient(circle,rgba(139,38,53,.13),transparent 70%);pointer-events:none}
  .inquiry-grid{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center;max-width:1200px;margin:0 auto}
  .inquiry-title{font-family:'Cormorant Garamond',serif;font-size:clamp(2rem,3vw,2.8rem);font-weight:700;color:#fff;line-height:1.2;margin-bottom:1.1rem}
  .inquiry-body{color:var(--text-dim);font-size:.97rem;line-height:1.8;margin-bottom:1.8rem}
  .inquiry-steps{list-style:none}
  .inquiry-step{display:flex;gap:.88rem;align-items:flex-start;margin-bottom:1.1rem}
  .step-num{width:26px;height:26px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,#8b2635,#a83245);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:700;color:var(--gold)}
  .step-text strong{color:var(--text);font-weight:600;display:block;font-size:.88rem;margin-bottom:.12rem}
  .step-text span{color:var(--text-dim);font-size:.82rem;line-height:1.5}

  /* ── HOW IT WORKS ── */
  .steps-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:2rem;position:relative}
  .steps-grid::before{content:'';position:absolute;top:23px;left:17%;right:17%;height:1px;background:linear-gradient(to right,rgba(139,38,53,.25),rgba(139,38,53,.55),rgba(139,38,53,.25))}
  .step-card{text-align:center;padding:1.25rem}
  .step-circle{width:48px;height:48px;border-radius:50%;margin:0 auto 1.3rem;background:linear-gradient(135deg,#8b2635,#a83245);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:1.3rem;font-weight:700;color:var(--gold);position:relative;z-index:1;box-shadow:0 0 0 4px var(--bg)}
  .step-icon{font-size:1.7rem;margin-bottom:.85rem}
  .step-title{font-size:1rem;font-weight:700;color:#fff;margin-bottom:.4rem}
  .step-outcome{font-size:.74rem;font-weight:600;color:#4caf50;background:rgba(76,175,80,.1);border:1px solid rgba(76,175,80,.2);display:inline-block;padding:.18rem .65rem;border-radius:10px;margin-bottom:.5rem}
  .step-desc{color:var(--text-dim);font-size:.86rem;line-height:1.6}

  /* ── ZERO FEES BANNER ── */
  .zero-banner{background:linear-gradient(135deg,rgba(46,125,50,.12),rgba(76,175,80,.06));border:1px solid rgba(76,175,80,.2);border-radius:12px;padding:1.4rem 2rem;margin-bottom:2.75rem;display:flex;align-items:center;justify-content:center;gap:2.5rem;flex-wrap:wrap;text-align:center}
  .zero-num{font-family:'Cormorant Garamond',serif;font-size:2.6rem;font-weight:700;background:linear-gradient(135deg,#4caf50,#2e7d32);-webkit-background-clip:text;-webkit-text-fill-color:transparent;line-height:1}
  .zero-label{font-size:.82rem;color:#4caf50;font-weight:600;margin-top:.2rem}
  .zero-desc{font-size:.88rem;color:var(--text-dim);max-width:420px;line-height:1.6}
  .zero-desc strong{color:var(--gold)}

  /* ── PRICING ── */
  .pricing-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1rem}
  .pricing-card{background:rgba(232,213,160,.03);border:1px solid rgba(232,213,160,.08);border-radius:12px;padding:1.75rem 1.5rem;position:relative;display:flex;flex-direction:column;transition:all .3s}
  .pricing-card:hover{transform:translateY(-3px);box-shadow:0 16px 40px rgba(0,0,0,.3)}
  .pricing-card.popular{border-color:var(--crimson);background:rgba(139,38,53,.07)}
  .popular-badge{position:absolute;top:-10px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,#8b2635,#a83245);color:var(--gold);font-size:.68rem;font-weight:700;padding:.22rem .9rem;border-radius:10px;white-space:nowrap;letter-spacing:.4px}
  .plan-for{font-size:.68rem;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--crimson-light);margin-bottom:.35rem}
  .plan-name{font-size:1.05rem;font-weight:700;color:#fff;margin-bottom:.45rem}
  .plan-seats{display:inline-flex;align-items:center;gap:.35rem;background:rgba(139,38,53,.14);border:1px solid rgba(139,38,53,.28);padding:.22rem .65rem;border-radius:12px;margin-bottom:.9rem;width:fit-content}
  .plan-seats-text{font-size:.7rem;font-weight:600;color:var(--text-dim)}
  .plan-price{font-family:'Cormorant Garamond',serif;font-size:2.2rem;font-weight:700;background:linear-gradient(135deg,#f4e8bc,#c8a84b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;line-height:1;margin-bottom:.15rem}
  .plan-period{font-size:.8rem;color:var(--text-dim);margin-bottom:.7rem}
  .plan-extra{font-size:.7rem;color:rgba(232,213,160,.4);margin-bottom:.7rem}
  .plan-desc{font-size:.8rem;color:var(--text-dim);line-height:1.5;margin-bottom:1.1rem}
  .plan-div{border:none;border-top:1px solid rgba(232,213,160,.07);margin-bottom:1.1rem}
  .plan-features{list-style:none;flex:1;margin-bottom:1.3rem}
  .plan-feature{display:flex;align-items:flex-start;gap:.45rem;font-size:.8rem;color:var(--text-dim);margin-bottom:.55rem;line-height:1.4}
  .feat-check{color:var(--gold-dim);flex-shrink:0;margin-top:.05rem}
  .feat-arrow{color:var(--crimson-light);flex-shrink:0}
  .plan-btn{width:100%;padding:.68rem;border-radius:24px;font-size:.88rem;font-weight:600;cursor:pointer;font-family:'Outfit',sans-serif;transition:all .2s}
  .plan-btn-primary{background:linear-gradient(135deg,#e8d5a0,#c8a84b);color:#0a0a0a;border:none}
  .plan-btn-primary:hover{box-shadow:0 6px 20px rgba(232,213,160,.3)}
  .plan-btn-outline{background:transparent;color:var(--gold-dim);border:1.5px solid rgba(232,213,160,.25)}
  .plan-btn-outline:hover{border-color:var(--gold-dim);color:var(--gold)}

  /* ── FAQ ── */
  .faq-list{max-width:660px;margin:0 auto}
  .faq-item{border-bottom:1px solid rgba(232,213,160,.07);overflow:hidden;background:transparent}
  .faq-q{width:100%;display:flex;justify-content:space-between;align-items:center;padding:1.25rem 0;background:transparent !important;border:none;cursor:pointer;color:#ffffff !important;font-size:.92rem;font-weight:500;font-family:'Outfit',sans-serif;text-align:left;gap:1rem}
  .faq-q:hover{color:#e8d5a0 !important}
  .faq-arrow{color:#b8a870;transition:transform .3s;flex-shrink:0;font-size:.88rem}
  .faq-arrow.open{transform:rotate(180deg)}
  .faq-a{color:#8a7e68 !important;font-size:.88rem;line-height:1.7;max-height:0;overflow:hidden;transition:max-height .35s ease,padding-bottom .2s ease;background:transparent}
  .faq-a.open{max-height:300px;padding-bottom:1.2rem}

  /* ── FINAL CTA ── */
  .final-cta{padding:6rem 2rem;text-align:center;background:radial-gradient(ellipse at center,rgba(139,38,53,.1) 0%,transparent 65%),var(--surface)}
  .cta-early{display:inline-block;margin-bottom:.9rem;background:rgba(232,213,160,.05);border:1px solid rgba(232,213,160,.12);border-radius:20px;padding:.35rem 1.1rem;font-size:.78rem;color:var(--text-dim);font-style:italic}
  .cta-title{font-family:'Cormorant Garamond',serif;font-size:clamp(2.2rem,4vw,3.4rem);font-weight:700;color:#fff;margin-bottom:.9rem}
  .cta-sub{color:var(--text-dim);font-size:.97rem;max-width:460px;margin:0 auto 2.2rem;line-height:1.7}
  .cta-btns{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;margin-bottom:.75rem}
  .cta-microcopy{font-size:.8rem;color:var(--text-dim)}
  .cta-link{color:var(--gold-dim);text-decoration:none}
  .cta-link:hover{text-decoration:underline}

  /* ── FOOTER ── */
  .footer{background:var(--bg);border-top:1px solid rgba(232,213,160,.06);padding:1.8rem 2rem}
  .footer-inner{max-width:1200px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem}
  .footer-logo{font-family:'Cormorant Garamond',serif;font-size:1.1rem;font-weight:700;letter-spacing:1.5px;background:linear-gradient(135deg,#f4e8bc,#c8a84b);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
  .footer-links{display:flex;gap:2rem;flex-wrap:wrap}
  .footer-link{color:var(--text-dim);font-size:.8rem;text-decoration:none;transition:color .2s}
  .footer-link:hover{color:var(--gold)}
  .footer-copy{font-size:.75rem;color:var(--text-dim);opacity:.5}

  /* ── SCROLL-TO-TOP FAB ── */
  .scroll-fab{position:fixed;bottom:1.5rem;right:1.5rem;z-index:500;opacity:0;transform:translateY(20px);pointer-events:none;transition:all .3s}
  .scroll-fab.visible{opacity:1;transform:translateY(0);pointer-events:all}
  .scroll-fab button{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,#e8d5a0,#c8a84b);color:#0a0a0a;border:none;font-size:1.1rem;font-weight:700;cursor:pointer;box-shadow:0 4px 16px rgba(232,213,160,.3);transition:all .2s}
  .scroll-fab button:hover{transform:scale(1.1)}

  /* ── FADE IN ANIMATION ── */
  .fade-in{opacity:0;transform:translateY(22px);transition:opacity .65s ease,transform .65s ease}
  .fade-in.visible{opacity:1;transform:translateY(0)}

  /* ── RESPONSIVE ── */
  @media(max-width:960px){
    .hero-inner{grid-template-columns:1fr;gap:3rem;padding-top:6rem}
    .hero-visual{display:none}
    .pillars-grid,.steps-grid,.inquiry-grid{grid-template-columns:1fr}
    .pricing-grid{grid-template-columns:1fr 1fr}
    .steps-grid::before{display:none}
    .nav-links{display:none}
    .nav-hamburger{display:flex}
    .db-actions{grid-template-columns:1fr 1fr}
  }
  @media(max-width:560px){
    .pricing-grid{grid-template-columns:1fr}
    .pain-items{flex-direction:column;align-items:center}
    .db-stats{grid-template-columns:repeat(3,1fr)}
    .inq-row2{grid-template-columns:1fr}
    .footer-inner{flex-direction:column;text-align:center}
    .footer-links{justify-content:center}
  }
`;

// ─── Data ────────────────────────────────────────────────────────────────────

const tiers = [
  {
    title: 'Basic', audience: 'Solo Artist', price: '$35', popular: false,
    seats: '1 Artist',
    desc: 'Everything a solo artist needs to go paperless and manage clients professionally.',
    features: ['Appointment Scheduling & Calendar', 'Client Management & Profiles', 'Digital Consent Forms', 'Custom Form Builder', 'Tattoo Inquiry Booking Link', 'Deposit Collection via Your Stripe'],
    extra: null,
  },
  {
    title: 'Standard', audience: 'Solo Artist + Automation', price: '$85', popular: true,
    seats: '1 Artist',
    desc: 'Automate your workflow so you can focus on creating art, not chasing clients.',
    features: ['Everything in Basic, plus:', 'Automated 24hr Appointment Reminders', 'Automated Aftercare Emails', 'Advanced Client Profiles', 'Inventory Management & Low-Stock Alerts', 'Customizable Inquiry Forms'],
    extra: null,
  },
  {
    title: 'Starter Studio', audience: 'Multi-Artist Shop', price: '$199', popular: false,
    seats: 'Up to 4 Artists',
    desc: 'Run your shop on one platform — shared clients, one calendar, everyone in sync.',
    features: ['All Basic features for each artist', 'Invite & Manage Artists (Up to 4)', 'Role-Based Permissions', 'Shared Clients, Forms & Calendar', 'Inventory Management', 'Customizable Inquiry Forms'],
    extra: '+ $25/mo per extra seat',
  },
  {
    title: 'Growth Studio', audience: 'Multi-Artist + Automation', price: '$299', popular: false,
    seats: 'Up to 4 Artists',
    desc: 'Full studio automation — reminders and aftercare handled for every artist.',
    features: ['Everything in Starter Studio, plus:', 'Automated 24hr Appointment Reminders', 'Automated Aftercare Emails', 'Advanced Client Profiles', 'Record Packets — full audit trail per appointment'],
    extra: '+ $40/mo per extra seat',
  },
];

const faqs = [
  { q: 'Which plan is right for me?', a: "Solo artist? Basic has everything you need including deposit collection. Want automated reminders and aftercare emails? Go Standard. Running a shop with multiple artists? Starter or Growth Studio gives you shared management for up to 4 artists — add more seats as you grow." },
  { q: 'How does deposit collection work?', a: "You connect your own Stripe account. When you schedule an appointment, set a deposit amount and send the client a secure payment link via email or text. They pay, the money goes directly to your bank — we never touch it or take a cut. You only pay Stripe's standard processing fee (2.9% + 30¢)." },
  { q: 'Are the consent forms legally compliant?', a: "Yes. Every form includes proper e-signature disclosure, complete audit trails with timestamps and IP addresses, and AES-256 encryption on all client data including ID photos. You can also customize forms to meet your local regulations." },
  { q: 'Can I cancel anytime?', a: "Absolutely. No long-term contracts, no cancellation fees. Your data remains accessible for 30 days after you cancel." },
  { q: 'What are the studio seat counts?', a: "Studio plans include the shop owner plus 3 additional artist seats (4 total). Need more? Add extra seats anytime — $25/month each on Starter Studio, $40/month each on Growth Studio." },
  { q: 'How do clients fill out consent forms?', a: "Consent forms are completed in-session from your account — you pull up the form, the client fills it out and signs digitally on the spot. Everything is saved automatically to their client profile with a full timestamp and audit trail." },
  { q: 'What happens when I upgrade or downgrade?', a: "Upgrades take effect immediately with prorated billing. Downgrades take effect at your next billing cycle. All your data is preserved when changing plans." },
];

const valuePillars = [
  {
    icon: '🖋️', title: 'Run Your Business', sub: 'All-in-one studio management',
    points: ['Appointments, clients & consent forms in one place', 'Custom drag-and-drop form builder', 'Inventory tracking with low-stock alerts', 'Collect deposits directly via your own Stripe'],
  },
  {
    icon: '🔒', title: 'Protect Your Business', sub: 'Legal compliance & data security',
    points: ['E-signatures with full audit trails & timestamps', 'AES-256 encryption on all client data & ID photos', 'Secure AWS cloud infrastructure', 'Customizable forms to meet local regulations'],
  },
  {
    icon: '📈', title: 'Grow Your Business', sub: 'Tools that scale with you',
    points: ['Automated appointment reminders & aftercare emails', 'Multi-artist studio management with shared resources', 'Role-based permissions for your team', 'Public inquiry link to capture new client requests'],
  },
];

const howItWorks = [
  { icon: '🎯', num: '1', title: 'Sign Up & Setup', outcome: 'Live in under 10 minutes', desc: 'Create your account, add your studio name and logo, connect your Stripe account. No tech skills needed.' },
  { icon: '📋', num: '2', title: 'Build Your Forms & Booking Link', outcome: 'No more paper chasing', desc: 'Drag-and-drop your consent forms, then share your public inquiry link — new clients can request appointments 24/7.' },
  { icon: '✅', num: '3', title: 'Manage & Let It Run', outcome: 'Fewer no-shows, less admin', desc: 'Track clients, collect deposits, and let automated reminders & aftercare emails handle the follow-up for you.' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function DashboardMockup() {
  const stats = [
    { label: 'Total Clients', num: '24', iconBg: 'rgba(255,23,68,.12)', icon: '👥' },
    { label: 'Appointments', num: '38', iconBg: 'rgba(41,121,255,.12)', icon: '📅' },
    { label: 'Consent Forms', num: null, iconBg: 'rgba(0,191,165,.12)', icon: '📄' },
    { label: 'Inventory', num: '61', iconBg: 'rgba(255,109,0,.12)', icon: '📦' },
    { label: 'Inquiries', num: '7', iconBg: 'rgba(124,77,255,.12)', icon: '💬', badge: 3 },
  ];
  return (
    <div className="db-frame">
      <div className="db-titlebar">
        <span className="dt dt-r" /><span className="dt dt-y" /><span className="dt dt-g" />
        <span className="db-urlbar">inkedupmanager.com/dashboard</span>
      </div>
      <div className="db-body">
        <div className="db-greeting-title">Good afternoon, Alex</div>
        <div className="db-greeting-sub">Here's what's happening with your studio today.</div>
        <div className="db-stats">
          {stats.map((s, i) => (
            <div className="db-stat" key={i}>
              {s.badge > 0 && <div className="db-pulse-badge">{s.badge}</div>}
              <div className="db-stat-icon" style={{ background: s.iconBg }}>{s.icon}</div>
              <div>
                <div className="db-stat-label">{s.label}</div>
                {s.num !== null
                  ? <div className="db-stat-num">{s.num}</div>
                  : <div style={{ fontSize: '.6rem', color: 'rgba(255,255,255,.3)', marginTop: '.12rem' }}>Manage forms</div>
                }
                {s.badge > 0 && <div className="db-new-chip">{s.badge} new</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="db-actions">
          <div className="db-action db-action-green">
            <div className="db-action-icon" style={{ background: 'rgba(67,160,71,.12)' }}>✅</div>
            <div><div className="db-action-title">Stripe Connected</div><div className="db-action-sub">Manage payments</div></div>
          </div>
          <div className="db-action db-action-blue">
            <div className="db-action-icon" style={{ background: 'rgba(41,121,255,.12)' }}>💳</div>
            <div><div className="db-action-title">Payment Link</div><div className="db-action-sub">Quick requests</div></div>
          </div>
          <div className="db-action db-action-purple">
            <div className="db-action-icon" style={{ background: 'rgba(124,77,255,.12)' }}>↗</div>
            <div><div className="db-action-title">Stripe Dashboard</div><div className="db-action-sub">Full management</div></div>
          </div>
        </div>
        <div className="db-section">
          <div className="db-section-hd">
            <div className="db-section-left">
              <div className="db-section-icon" style={{ background: 'rgba(41,121,255,.12)' }}>📅</div>
              <div>
                <div className="db-section-title">Upcoming Appointments</div>
                <div className="db-section-sub">3 in the next 7 days</div>
              </div>
            </div>
            <span className="db-chip-blue">3 upcoming</span>
          </div>
          {[
            { name: 'Marcus T.', time: '10:00 AM', badge: 'Today', bc: 'badge-today' },
            { name: 'Jordan L.', time: '1:30 PM', badge: 'Tomorrow', bc: 'badge-tmrw' },
            { name: 'Casey R.', time: '3:00 PM', badge: 'In 5 days', bc: 'badge-days' },
          ].map((a, i) => (
            <div className="db-appt-item" key={i}>
              <div className="db-appt-avatar">{a.name[0]}</div>
              <div>
                <div className="db-appt-name">{a.name}<span className={`db-appt-badge ${a.bc}`}>{a.badge}</span></div>
                <div className="db-appt-time">⏰ {a.time}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InquiryFormMockup() {
  return (
    <div className="inq-frame">
      <div className="inq-browser-bar">
        <span className="inq-dt inq-dt-r" /><span className="inq-dt inq-dt-y" /><span className="inq-dt inq-dt-g" />
        <span className="inq-url">inkedupmanager.com/inquiry/studio/ink-haven</span>
      </div>
      <div className="inq-body">
        <div className="inq-header-paper">
          <div className="inq-title">Request an Appointment</div>
          <div className="inq-studio">Ink Haven Studio</div>
        </div>
        <div className="inq-form-paper">
          <div className="inq-sec">👤 Your Information</div>
          <div className="inq-row2">
            <div className="inq-field">
              <div className="inq-lbl">Your Name *</div>
              <div className="inq-inp">
                <span className="inq-inp-icon">👤</span>
                <input className="inq-input" defaultValue="Alex Rivera" readOnly />
              </div>
            </div>
            <div className="inq-field">
              <div className="inq-lbl">Email Address *</div>
              <div className="inq-inp">
                <span className="inq-inp-icon">✉️</span>
                <input className="inq-input" defaultValue="alex@email.com" readOnly />
              </div>
            </div>
          </div>
          <div className="inq-row2">
            <div className="inq-field">
              <div className="inq-lbl">Phone (Optional)</div>
              <div className="inq-inp">
                <span className="inq-inp-icon">📞</span>
                <input className="inq-input" placeholder="(555) 000-0000" readOnly />
              </div>
            </div>
            <div className="inq-field">
              <div className="inq-lbl">How did you hear about me?</div>
              <select className="inq-select"><option>Instagram</option></select>
            </div>
          </div>
          <hr className="inq-divider" />
          <div className="inq-sec">📋 Project Details</div>
          <div className="inq-row1">
            <div className="inq-field">
              <div className="inq-lbl">Describe Your Project *</div>
              <div className="inq-textarea-wrap">
                <span className="inq-ta-icon">📝</span>
                <textarea className="inq-textarea" defaultValue="Full sleeve, traditional roses and dagger, black and grey..." readOnly />
              </div>
            </div>
          </div>
          <div className="inq-row2">
            <div className="inq-field">
              <div className="inq-lbl">Budget Range</div>
              <select className="inq-select"><option>$500 – $1,000</option></select>
            </div>
            <div className="inq-field">
              <div className="inq-lbl">Preferred Timeframe</div>
              <select className="inq-select"><option>1–2 months</option></select>
            </div>
          </div>
          <button className="inq-submit">📤 Submit Request</button>
          <div className="inq-note">Sent directly to the artist's dashboard — no DM required</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated } = useAuth0();

  const [scrolled, setScrolled] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  // ── Inject styles once ────────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = FONTS + css;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Scroll listeners ──────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      setShowFab(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Intersection observer for fade-ins ────────────────────────────────────
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-in').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  // ── Lock body scroll when mobile drawer open ──────────────────────────────
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // ── Auth handlers (matching original LandingPage.js) ─────────────────────
  const handleSignUp = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      loginWithRedirect({
        screen_hint: 'signup',
        prompt: 'login',
        max_age: 0,
        appState: { returnTo: '/dashboard', signup: true },
      });
    }
  };

  const handleLogin = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      loginWithRedirect({ appState: { returnTo: '/dashboard' } });
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const navItems = [
    { label: 'Features', id: 'features' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <div style={{ background: '#0a0a0a', minHeight: '100vh', color: '#e8d5a0' }}>
      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">
          <button className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            InkedUp Manager
          </button>

          {/* Desktop nav */}
          <div className="nav-links">
            {navItems.map(({ label, id }) => (
              <button key={id} className="nav-link" onClick={() => scrollTo(id)}>{label}</button>
            ))}
            {isAuthenticated ? (
              <button className="nav-dashboard" onClick={() => navigate('/dashboard')}>Dashboard</button>
            ) : (
              <>
                <button className="nav-dashboard" onClick={handleLogin}>Log In</button>
                <button className="nav-cta" onClick={handleSignUp}>Sign Up</button>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            className={`nav-hamburger${mobileOpen ? ' open' : ''}`}
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── MOBILE DRAWER ────────────────────────────────────────────────── */}
      <div className={`mobile-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />
      <div className={`mobile-drawer${mobileOpen ? ' open' : ''}`}>
        <button className="mobile-drawer-close" onClick={() => setMobileOpen(false)}>✕</button>
        {navItems.map(({ label, id }) => (
          <button key={id} className="mobile-nav-link" onClick={() => scrollTo(id)}>{label}</button>
        ))}
        {isAuthenticated ? (
          <button className="mobile-cta" onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>
            Go to Dashboard
          </button>
        ) : (
          <>
            <button className="mobile-cta" onClick={() => { handleSignUp(); setMobileOpen(false); }}>Sign Up</button>
            <button className="mobile-dashboard" onClick={() => { handleLogin(); setMobileOpen(false); }}>Log In</button>
          </>
        )}
      </div>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-inner">
          <div>
            <div className="hero-eyebrow">✦ Built for Tattoo Studios</div>
            <h1 className="hero-h1">
              Stop Chasing Paper.<br />
              <span className="gold-grad">Start Running Your Studio.</span>
            </h1>
            <p className="hero-sub">
              Digital consent forms, client management, appointment scheduling,
              and <strong>deposit collection with 0% platform fees</strong> — purpose-built for tattoo studios.
            </p>
            <div className="hero-btns">
              <button className="btn-primary" onClick={handleSignUp}>Get Started</button>
              <button className="btn-outline" onClick={() => scrollTo('pricing')}>View Plans</button>
            </div>
            <div className="hero-microcopy">Cancel anytime · No long-term contracts</div>
            <div className="hero-trust">
              {['256-bit encryption', 'Direct Stripe payouts', '0% platform fees', 'AWS cloud hosted'].map((t, i) => (
                <div className="trust-item" key={i}><span className="trust-dot" /><span className="trust-text">{t}</span></div>
              ))}
            </div>
          </div>
          <div className="hero-visual fade-in"><DashboardMockup /></div>
        </div>
      </section>

      {/* ── PAIN STRIP ───────────────────────────────────────────────────── */}
      <div className="pain-strip">
        <div className="pain-inner">
          <div className="pain-eyebrow">Sound familiar?</div>
          <h2 className="pain-title">The old way is costing you time and clients</h2>
          <div className="pain-items">
            {['Paper consent forms getting lost', 'No-shows with no deposit', 'Manually texting reminders', 'Nowhere to track client history', 'Missing new booking requests'].map((p, i) => (
              <div className="pain-item" key={i}><span className="pain-x">✕</span>{p}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── VALUE PILLARS ────────────────────────────────────────────────── */}
      <section id="features" className="section section-alt">
        <div className="section-inner">
          <div className="section-header fade-in">
            <span className="eyebrow">The Platform</span>
            <h2 className="section-title">Built for <span className="gold-grad">Tattoo Artists</span></h2>
            <p className="section-sub">One platform to manage your clients, protect your paperwork, and grow your studio</p>
          </div>
          <div className="pillars-grid">
            {valuePillars.map((p, i) => (
              <div className="pillar-card fade-in" key={i} style={{ transitionDelay: `${i * 0.12}s` }}>
                <div className="pillar-icon">{p.icon}</div>
                <div className="pillar-title">{p.title}</div>
                <div className="pillar-sub">{p.sub}</div>
                <ul className="pillar-points">
                  {p.points.map((pt, j) => <li key={j}><span className="check">✓</span>{pt}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TATTOO INQUIRY FEATURE ───────────────────────────────────────── */}
      <section className="inquiry-section">
        <div className="inquiry-grid">
          <div className="fade-in">
            <span className="eyebrow">Booking Requests</span>
            <h2 className="inquiry-title">Your Personal<br /><span className="gold-grad">Inquiry Link</span></h2>
            <p className="inquiry-body">
              Share one link on Instagram, your website, or anywhere — and clients can submit
              tattoo requests 24/7. You wake up to new inquiries in your dashboard, not buried in DMs.
            </p>
            <ul className="inquiry-steps">
              {[
                { title: 'Client fills out your custom form', desc: 'Style, placement, size, budget, reference images — you control what you ask.' },
                { title: 'You review requests in your dashboard', desc: 'See every inquiry in one place. Accept, decline, or reply directly from the app.' },
                { title: 'Convert to an appointment + deposit', desc: 'One click to book the session and send a Stripe deposit request to the client.' },
              ].map((s, i) => (
                <li className="inquiry-step" key={i}>
                  <div className="step-num">{i + 1}</div>
                  <div className="step-text"><strong>{s.title}</strong><span>{s.desc}</span></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="fade-in" style={{ transitionDelay: '.18s' }}><InquiryFormMockup /></div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section id="how-it-works" className="section">
        <div className="section-inner">
          <div className="section-header fade-in">
            <span className="eyebrow">Setup</span>
            <h2 className="section-title">Running in <span className="gold-grad">3 Simple Steps</span></h2>
            <p className="section-sub">From signup to managing your first client in under 10 minutes</p>
          </div>
          <div className="steps-grid">
            {howItWorks.map((s, i) => (
              <div className="step-card fade-in" key={i} style={{ transitionDelay: `${i * 0.15}s` }}>
                <div className="step-circle">{s.num}</div>
                <div className="step-icon">{s.icon}</div>
                <div className="step-title">{s.title}</div>
                <div className="step-outcome">{s.outcome}</div>
                <p className="step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="section section-alt">
        <div className="section-inner">
          <div className="section-header fade-in">
            <span className="eyebrow">Pricing</span>
            <h2 className="section-title">Choose Your <span className="gold-grad">Plan</span></h2>
            <p className="section-sub">Solo artist or multi-artist shop — pick the plan that fits how you work</p>
          </div>
          <div className="zero-banner fade-in">
            <div>
              <div className="zero-num">0%</div>
              <div className="zero-label">Platform Fee on Payments</div>
            </div>
            <div className="zero-desc">
              Unlike platforms that take <strong>5–15% of every transaction</strong>, you connect your own Stripe
              account and deposits go <strong>directly to your bank</strong>. We never touch your money.
            </div>
          </div>
          <div className="pricing-grid">
            {tiers.map((tier, i) => (
              <div className={`pricing-card fade-in${tier.popular ? ' popular' : ''}`} key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                {tier.popular && <div className="popular-badge">⚡ Most Popular</div>}
                <div className="plan-for">{tier.audience}</div>
                <div className="plan-name">{tier.title}</div>
                <div className="plan-seats">
                  <span className="plan-seats-text">{tier.seats}</span>
                </div>
                <div className="plan-price">{tier.price}</div>
                <div className="plan-period">/month · cancel anytime</div>
                {tier.extra && <div className="plan-extra">{tier.extra}</div>}
                <div className="plan-desc">{tier.desc}</div>
                <hr className="plan-div" />
                <ul className="plan-features">
                  {tier.features.map((f, j) => (
                    <li className="plan-feature" key={j}>
                      {j === 0 && f.includes('Everything')
                        ? <span className="feat-arrow">→</span>
                        : <span className="feat-check">✓</span>
                      }
                      <span style={j === 0 && f.includes('Everything') ? { fontWeight: 600, color: 'rgba(232,213,160,.7)' } : {}}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`plan-btn ${tier.popular ? 'plan-btn-primary' : 'plan-btn-outline'}`}
                  onClick={handleSignUp}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="section" style={{ background: '#0a0a0a' }}>
        <div className="section-inner">
          <div className="section-header fade-in">
            <span className="eyebrow">FAQ</span>
            <h2 className="section-title">Frequently Asked <span className="gold-grad">Questions</span></h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div className="faq-item fade-in" key={i}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  <span className={`faq-arrow${openFaq === i ? ' open' : ''}`}>▼</span>
                </button>
                <div className={`faq-a${openFaq === i ? ' open' : ''}`}>{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <div className="final-cta">
        <div className="cta-early">✦ Be one of the first studios to go fully digital</div>
        <h2 className="cta-title">
          Ready to Run Your Studio<br />
          <span className="gold-grad">Without the Paperwork?</span>
        </h2>
        <p className="cta-sub">
          Get set up in under 10 minutes. No long-term contract, no cancellation fees.
          If it doesn't save you time in the first month, you can walk away — no questions asked.
        </p>
        <div className="cta-btns">
          <button className="btn-primary" style={{ fontSize: '1.05rem', padding: '1rem 2.5rem' }} onClick={handleSignUp}>
            Get Started
          </button>
          <button className="btn-outline" onClick={() => scrollTo('pricing')}>Compare Plans</button>
        </div>
        <div className="cta-microcopy">
          Questions?{' '}
          <a href="mailto:sales@inkedupmanager.com" className="cta-link">sales@inkedupmanager.com</a>
        </div>
      </div>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="footer">
        <div className="footer-inner">
          <span className="footer-logo">InkedUp Manager</span>
          <div className="footer-links">
            {[
              { label: 'Terms & Conditions', href: '/legal/terms-and-conditions' },
              { label: 'Privacy Policy', href: '/legal/privacy-policy' },
              { label: 'Artist Terms', href: '/legal/artist-terms' },
            ].map(({ label, href }) => (
              <a key={label} href={href} className="footer-link">{label}</a>
            ))}
          </div>
          <span className="footer-copy">© 2026 InkedUp Manager. All rights reserved.</span>
        </div>
      </footer>

      {/* ── SCROLL-TO-TOP FAB ────────────────────────────────────────────── */}
      <div className={`scroll-fab${showFab ? ' visible' : ''}`}>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top">↑</button>
      </div>
    </div>
  );
};

export default LandingPage;